// @ts-nocheck
import { ReactNode, useState, useRef } from 'react';
import { Box } from '@mantine/core';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { useReducedMotion } from '@/components/animations';

interface Floating3DCardProps {
  children: ReactNode;
  /** Maximum tilt angle in degrees */
  maxTilt?: number;
  /** Perspective distance */
  perspective?: number;
  /** Transition speed in ms */
  transitionSpeed?: number;
  /** Enable/disable the floating shadow */
  floatingShadow?: boolean;
  /** Card padding */
  padding?: 'sm' | 'md' | 'lg' | 'xl';
  /** Border radius */
  radius?: 'sm' | 'md' | 'lg' | 'xl';
  /** Enable subtle glow effect */
  glow?: boolean;
  /** Glow color */
  glowColor?: string;
}

export default function Floating3DCard({
  children,
  maxTilt = 15,
  perspective = 1000,
  transitionSpeed = 300,
  floatingShadow = true,
  padding = 'xl',
  radius = 'lg',
  glow = false,
  glowColor,
}: Floating3DCardProps) {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const reduceMotion = useReducedMotion();

  const cardRef = useRef<HTMLDivElement>(null);
  const [transform, setTransform] = useState({ rotateX: 0, rotateY: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const paddingValues = {
    sm: theme.spacing.sm,
    md: theme.spacing.md,
    lg: theme.spacing.lg,
    xl: theme.spacing.xl,
  };

  const radiusValues = {
    sm: theme.radius.sm,
    md: theme.radius.md,
    lg: theme.radius.lg,
    xl: theme.radius.xl,
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (reduceMotion || !cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;

    const rotateX = (mouseY / (rect.height / 2)) * -maxTilt;
    const rotateY = (mouseX / (rect.width / 2)) * maxTilt;

    setTransform({ rotateX, rotateY });
  };

  const handleMouseEnter = () => {
    if (!reduceMotion) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setTransform({ rotateX: 0, rotateY: 0 });
  };

  const defaultGlowColor = glowColor || (isDark ? theme.colors.indigo[4] : theme.colors.indigo[5]);

  const getShadow = () => {
    if (!floatingShadow) return 'none';

    const baseShadow = isDark
      ? '0 10px 30px rgba(0, 0, 0, 0.4)'
      : '0 10px 30px rgba(0, 0, 0, 0.15)';

    const hoverShadow = isDark
      ? '0 20px 50px rgba(0, 0, 0, 0.5)'
      : '0 20px 50px rgba(0, 0, 0, 0.25)';

    const glowShadow = glow ? `, 0 0 30px ${defaultGlowColor}40` : '';

    return isHovered ? `${hoverShadow}${glowShadow}` : baseShadow;
  };

  return (
    <Box
      style={{
        perspective: `${perspective}px`,
      }}
    >
      <Box
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          backgroundColor: isDark ? theme.colors.dark[7] : theme.colors.white,
          borderRadius: radiusValues[radius],
          padding: paddingValues[padding],
          boxShadow: getShadow(),
          border: `1px solid ${isDark ? theme.colors.dark[5] : theme.colors.gray[2]}`,
          transform: reduceMotion
            ? 'none'
            : `rotateX(${transform.rotateX}deg) rotateY(${transform.rotateY}deg) ${isHovered ? 'translateZ(10px)' : 'translateZ(0)'}`,
          transition: `transform ${transitionSpeed}ms ease-out, box-shadow ${transitionSpeed}ms ease-out`,
          transformStyle: 'preserve-3d',
          willChange: 'transform',
        }}
      >
        {/* Inner content with slight 3D offset */}
        <Box
          style={{
            transform: reduceMotion ? 'none' : 'translateZ(20px)',
            transformStyle: 'preserve-3d',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
